import { auditReports } from '@mi-front/api-clients';
import { Autocomplete, TextField } from '@mui/material';
import { useIntl } from 'react-intl';

/**
 * Props for the AOD Rules Autocomplete component.
 */
export interface AodRulesAutocompleteProps {
  /** The selected rule ID */
  value: string | null;
  /** Callback when the selected rule changes */
  onChange: (ruleId: string | null) => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Custom label for the autocomplete */
  label?: string;
  /** Custom placeholder text */
  placeholder?: string;
  /** Custom styles */
  sx?: object;
}

/**
 * Autocomplete component for selecting AOD rules.
 * Fetches all available AOD rules and displays them in an autocomplete dropdown.
 */
export function AodRulesAutocomplete({
  value,
  onChange,
  disabled = false,
  label,
  placeholder,
  sx,
}: AodRulesAutocompleteProps) {
  const { $t } = useIntl();

  // Fetch all AOD rules (with a high limit to get all rules)
  const { data, isLoading } = auditReports.useGetManyBaseAodRulesControllerAodRule({
    limit: 1000, // High limit to get all rules
  });

  const rules = data?.data || [];
  const selectedRule = rules.find((rule) => rule.id === value) || null;

  // Sort rules alphabetically by name
  const sortedRules = [...rules].sort((a, b) => a.name.localeCompare(b.name));

  const handleChange = (_event: React.SyntheticEvent, newValue: auditReports.AodRule | null) => {
    onChange(newValue?.id || null);
  };

  return (
    <Autocomplete
      size="small"
      value={selectedRule}
      options={sortedRules}
      loading={isLoading}
      disabled={disabled}
      onChange={handleChange}
      getOptionLabel={(option: auditReports.AodRule) => option.name}
      isOptionEqualToValue={(option: auditReports.AodRule, val: auditReports.AodRule) =>
        option.id === val.id
      }
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
      sx={sx}
    />
  );
}
